import React, { useState } from 'react';
import { Modal, Progress } from 'antd';
import { useTranslation } from 'react-i18next';

interface SurveySubmissionModalProps {
  modalVisible: any;
  onModalSubmission: () => void | Promise<void>;
  setModalVisible: any;
  surveyModel: any;
  surveyRes: any;
  /**
   * If true, users are always shown a 'once submitted, cannot be edited'
   * message (even for surveys that are normally editable, e.g. general health).
   */
  neverShowEditableMessage: boolean;
  completingPercent: number;
  progressMessage?: string;
  title?: string;
}

/** Modal component used when confirming the submission of a survey. */
function SurveySubmissionModal({
  modalVisible,
  onModalSubmission,
  setModalVisible,
  surveyModel,
  surveyRes,
  neverShowEditableMessage,
  completingPercent,
  progressMessage = '',
  title = 'Submitting Survey'
}: SurveySubmissionModalProps) {
  const { t } = useTranslation('common');

  const [completingSurvey, setCompletingSurvey] = useState(false);

  const showEditableMessageWhenApplicable = !neverShowEditableMessage;

  const alwaysEditable = showEditableMessageWhenApplicable && surveyRes.always_edit;

  const handleModalCancel = () => {
    surveyModel.clear(false, true);
    setModalVisible(false);
  };

  const handleModalConfirm = async () => {
    setCompletingSurvey(true);

    try {
      await onModalSubmission();
    } finally {
      setCompletingSurvey(false);
      setModalVisible(false);
    }
  };

  return (
    <Modal
      title={title}
      centered
      open={modalVisible}
      closable={alwaysEditable}
      onCancel={alwaysEditable && handleModalCancel}
      cancelButtonProps={
        !alwaysEditable
          ? { style: { display: 'none' }, disabled: completingPercent < 100 }
          : { disabled: completingPercent < 100 }
      }
      onOk={handleModalConfirm}
      okText={alwaysEditable ? 'Submit' : 'Continue'}
      okButtonProps={{ loading: completingSurvey, disabled: completingPercent < 100 }}
      data-testid="submission-modal-component"
    >
      {completingPercent < 100 ? (
        <>
          <Progress percent={completingPercent} />
          <p>{progressMessage}</p>
        </>
      ) : (
        <>
          {alwaysEditable
            ? 'You can come back and update your answers if anything changes. Are you happy that this assessment is complete?'
            : t('Thank you for sending your answers. You can see these in the Documents area.')}
        </>
      )}
    </Modal>
  );
}

export default SurveySubmissionModal;
